import { REQUIRED_STATUS } from '@organisms/administrator/partials/section/content/objects/root/basicProfile/constants';
import { SidePanelSelect, TPanelContainer, TSidePanelOption } from '@molecules/sidePanelSelect';

type Props = {
    item: any;
    selectedValues: Record<number, number>;
    valueLabels: Record<number, string>;
    customOptions: Record<number, TSidePanelOption[]>;
    searchTexts: Record<number, string>;
    openStates: Record<number, boolean>;

    setOpenStates: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
    setValueLabels: React.Dispatch<React.SetStateAction<Record<number, string>>>;
    setSearchTexts: React.Dispatch<React.SetStateAction<Record<number, string>>>;

    getSelectRef: (id: number) => React.RefObject<any>;
    handleSearch: (id: number, text: string) => void;
    handleValueChange: (id: number, value: number) => void;
    addCustomOption: (id: number, value: string, ref: React.RefObject<any>) => void;
    onFieldBlur?: () => void;
    /** Границы панели вариантов; задаёт владелец — обычно это модалка. */
    panelContainer?: TPanelContainer;
};

export const SelectField = ({
    item,
    selectedValues,
    valueLabels,
    customOptions,
    searchTexts,
    openStates,
    setOpenStates,
    setValueLabels,
    setSearchTexts,
    getSelectRef,
    handleSearch,
    handleValueChange,
    addCustomOption,
    onFieldBlur,
    panelContainer,
}: Props) => {
    // бэк отдаёт status значений и числом (1/0), и булевым (true/false) —
    // показываем все значения, которые пришли в ответе
    const baseOptions = (item.values || []).map(({ value, id }) => ({
        label: value,
        value: Number(id),
    }));

    const selectedValue = selectedValues[item.id];
    // id опции может прийти строкой с одного эндпоинта (сущность) и числом с другого
    // (категория) — сравниваем по Number(), иначе уже существующий в baseOptions
    // вариант не находится и задублируется в конце списка
    const existingValueOption =
        selectedValue !== undefined &&
        valueLabels[selectedValue] &&
        !baseOptions.some((option) => Number(option.value) === Number(selectedValue))
            ? [{ label: valueLabels[selectedValue], value: selectedValue }]
            : [];

    const opts = [...baseOptions, ...existingValueOption, ...(customOptions[item.id] || [])].filter(
        (option, index, self) =>
            self.findIndex((o) => Number(o.value) === Number(option.value)) === index
    );

    const searchText = searchTexts[item.id] || '';
    const lowered = searchText.toLowerCase();
    const filtered = lowered ? opts.filter((o) => o.label.toLowerCase().includes(lowered)) : opts;

    const isRequired = item.status === REQUIRED_STATUS;
    const hasValue = selectedValues[item.id] !== undefined;

    const open = openStates[item.id] || false;
    const setOpen = (isOpen: boolean) => {
        setOpenStates((prev) => ({ ...prev, [item.id]: isOpen }));
    };
    const selectRef = getSelectRef(item.id);

    const handleCreateOption = (label: string) => {
        addCustomOption(item.id, label, selectRef);
        setOpen(false);
    };

    return (
        <SidePanelSelect
            options={filtered}
            value={hasValue ? selectedValue : undefined}
            onChange={(value, option) => {
                if (option?.label) {
                    setValueLabels((prev) => ({ ...prev, [value as number]: option.label }));
                }
                handleValueChange(item.id, value as number);
                setSearchTexts((prev) => ({ ...prev, [item.id]: '' }));
            }}
            searchValue={searchText}
            onSearch={(text) => handleSearch(item.id, text)}
            open={open}
            onOpenChange={setOpen}
            allowClear={!isRequired}
            hasError={isRequired && !hasValue}
            onCreateOption={handleCreateOption}
            panelContainer={panelContainer}
            onBlur={() => {
                // Набранное, но не выбранное значение заводим своим — так же,
                // как делал прежний antd Select по blur
                if (searchText.trim()) {
                    addCustomOption(item.id, searchText.trim(), selectRef);
                    setTimeout(() => onFieldBlur?.(), 0);
                    return;
                }
                onFieldBlur?.();
            }}
        />
    );
};
